import {ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnInit, Output, TemplateRef} from '@angular/core';
import {ActionButtonType, ButtonSize, DisplayType, WaterfallConfig} from './waterfall-config';
import {RxComponent} from 'app/core/components/common/rx.component';
import {Observable, ReplaySubject, Subject} from 'rxjs';
import {SmartModalService} from 'app/core/services/smart-modal.service';
import {FormGroup} from '@angular/forms';

@Component({
    selector: 'itpx-tandem',
    templateUrl: './waterfall.component.html',
    styleUrls: ['./waterfall.component.css']
})
export class WaterfallComponent extends RxComponent implements OnInit {
    @Input() data: WaterfallConfig;
    @Input() breadcrumbValues: Observable<{ parentChoice: string, ancestorHistory: { name: string, levelTag: string }[] }>
    @Input() templates: { [key: string]: TemplateRef<ElementRef> } = {};
    @Input() formGroups: { [key: string]: FormGroup } = {};
    @Output() cancel = new EventEmitter<void>();
    @Output() finish = new EventEmitter<any>();
    @Output() breadCrumbEmitter = new EventEmitter<{ name: string, levelTag: string }>();

    clickBackInternal = new Subject<void>();
    breadcrumbs: Observable<{ name: string, levelTag: string }[]>;
    ancestorHistorySubject = new ReplaySubject<{ parentChoice: string, ancestorHistory: { name: string, levelTag: string }[] }>(1);
    actionButtonType: typeof ActionButtonType = ActionButtonType;
    selectedValue: any;
    hideMe: boolean;
    input: Observable<any[]>;
    nameChoice: ReplaySubject<string>;
    readonly cutoffLimit: number = 20;
    displayType: typeof DisplayType = DisplayType;
    buttonSize: typeof ButtonSize = ButtonSize;

    constructor(private modalService: SmartModalService,
                private detector: ChangeDetectorRef
    ) {
        super()
    }

    ngOnInit() {
        this.input = this.data.input.shareReplay(1);
        let passThrough = this.input.map(values => !!(this.data.skipDefault && values && 2 > values.length)).shareReplay(1);
        this.nameChoice = new ReplaySubject<string>(1);
        if (this.data.onItemClicked) {
            this.data.onItemClicked.subscribe(v => this.nameChoice.next(this.data.nameExtractor(v)));
        }
        if (this.data.isRoot) {
            this.breadcrumbValues = Observable.of(this.data.rootBreadcrumb).shareReplay(1);
        }

        this.breadcrumbs = Observable.zip(this.breadcrumbValues, passThrough)
            .map(([breadcrumbValues, isPassThrough]) => this.breadcrumbFormatter(breadcrumbValues, isPassThrough));

        Observable.combineLatest(this.breadcrumbs, this.nameChoice)
            .bindToLifecycle(this)
            .subscribe(([historyPrime, itemClicked]) => {
                this.ancestorHistorySubject.next(({ancestorHistory: historyPrime, parentChoice: itemClicked}));
            });
        passThrough.zip(this.input).bindToLifecycle(this).subscribe(([isPassthrough, values]) => {
            if (isPassthrough) {
                if (this.data.isRoot && this.data.child) {  // Dynamically promote to Cancel button
                    let backButton = this.data.child.display.buttons.find(v => ActionButtonType.Back === v.type);
                    if (backButton) {
                        backButton.name = 'Cancel';
                    }
                }
                this.onClick(values && values.length ? values[0] : {});
            } else {
                this.hideMe = false
            }
        });
        this.clickBackInternal.withLatestFrom(passThrough).bindToLifecycle(this).subscribe(([_, isPassThrough]) => {
            if (isPassThrough) {
                this.onBack();
            }
            this.hideMe = false;
        });
        if (this.data.onClickOverrideSubject) {
            this.data.onClickOverrideSubject.neverError().bindToLifecycle(this).subscribe(val => this.onClick(val))
        }
        /*if (this.hasFormGroup() && !this.data.manualProceed) {
            this.formGroups[this.data.formGroupName].valueChanges.subscribe(value => this.onClick(value));
        }*/
    }

    private hasFormGroup() {
        return this.data.formGroupName && this.formGroups[this.data.formGroupName];
    }

    breadcrumbFormatter(breadcrumbValues, isPassThrough) {
        let historyPrime: { name: string, levelTag: string }[] = JSON.parse(JSON.stringify(breadcrumbValues.ancestorHistory)); // deep copy the array
        if (!isPassThrough && breadcrumbValues.parentChoice) {
            historyPrime.push({levelTag: this.data.levelTag, name: breadcrumbValues.parentChoice});
        }
        return historyPrime;
    }

    onClick(event: any) {
        this.selectedValue = event;
        if (this.hasFormGroup && this.data.formDriven) {
            this.formGroups[this.data.formGroupName].patchValue(this.data.mapChoiceToForm(event), {onlySelf: true, emitEvent: false})
        }
        this.hideMe = true;
        if (this.data.child) {
            this.data.onItemClicked.next(event);
        } else {
            this.onChildFinish(null);
        }
    }

    onBack() {
        this.hideMe = true;
        let backButtonConfig = this.data.display.buttons.find(button => this.actionButtonType.Back === button.type);
        if (backButtonConfig && backButtonConfig.listener) {
            backButtonConfig.listener.next();
        }
        if (this.hasFormGroup()) {
            this.formGroups[this.data.formGroupName].reset(this.data.resetSeed, this.data.resetPropagate);
        }
        if (this.data.isRoot) {
            this.modalService.close();
        } else {
            this.cancel.emit();
        }
    }

    onFinish() {
        // TODO: More thinking required? Called by finished button click. Aimed for select multi property and sticky buttons
        this.hideMe = true;
        if (this.data.isRoot) {
            this.modalService.sendResult({values: this.selectedValue, nested: null});
        } else {
            this.finish.emit({values: this.selectedValue, nested: null});
        }
    }

    onChildBack() {
        this.clickBackInternal.next();

    }

    onBreadCrumbClick(event: { name: string, levelTag: string }) {
        if (event.levelTag === this.data.levelTag) {
            this.hideMe = false;
            this.detector.detectChanges();
        } else {
            this.hideMe = true;
            this.breadCrumbEmitter.emit(event);
        }
    }

    onChildFinish(event: any) {
        if (this.data.isRoot) {
            this.modalService.sendResult({values: this.selectedValue, nested: event});
        } else {
            this.finish.emit({values: this.selectedValue, nested: event});
        }
    }

    onNext() {
        this.hideMe = true;
    }

    trackByFn(index, item) {
        if (!item) return null;
        return index;
    }

    addSizeStyle(input: ButtonSize) {
        let resp;
        switch (input) {
            case ButtonSize.Lg:
                resp = 'box-lg';
                break;
            case ButtonSize.Sm:
                resp = 'box-sm';
                break;
            case ButtonSize.Custom:
            case ButtonSize.Xl:
                throw new Error('TBI');
            case ButtonSize.Md:
            default:
                resp = 'box-md'
                break;
        }
        return resp;
    }

    addDefaultSvgStyle(input: ButtonSize) {
        let resp;
        switch (input) {
            case ButtonSize.Lg:
                resp = 'defaultIcon-lg';
                break;
            case ButtonSize.Custom:
            case ButtonSize.Sm:
            case ButtonSize.Xl:
                throw new Error('TBI');
            case ButtonSize.Md:
            default:
                resp = 'defaultIcon-md'
                break;
        }
        return resp;
    }

    onCustomButtonAction(button: any) {
        if(button.listener) {
            button.listener.next();
            if (button.action) {
                button.action.take(1).subscribe(action => {
                    switch (action) {
                        case ActionButtonType.Finish:
                            this.onFinish();
                            break;
                        case ActionButtonType.Back:
                            this.onBack();
                            break;
                        case ActionButtonType.Next:
                            this.onNext();
                            break;
                        case ActionButtonType.Custom:
                            break;
                        default:
                            throw new Error(`Unknown action ${action}`);
                    }
                })
            }
        }
    }
}



// WEBPACK FOOTER //
// ./src/app/shared/components/waterfall/waterfall.component.ts